import type {Module} from "vuex";
import type {RootState} from "@/store/types";
import type {PaginatedResult} from "./impersonation/orders";
import {order} from "@/api/order";
import {retryRequest} from "@/helpers/request";
import emitter from "@/api/events";

export interface OrderProduct {
    sku?: string
    quantity?: number
    [key: string]: unknown
}

// Order as returned by the order service
export interface OrderResponseItem {
    _id: string
    products: OrderProduct[]
    [key: string]: unknown
}

export interface Order extends OrderResponseItem {
    id: string
}

export interface OrderTransferPayload {
    orderProductId: string
    email: string
    reason: string
}

export interface OrderRequirementsPayload {
    items: {sku: string, quantity: number}[]
    country: string
}

export interface OrdersState {
    loading: boolean
    items: Order[]
}

const format = (items: OrderResponseItem[]): Order[] => items.map(item => ({...item, id: item._id}))

const orders: Module<OrdersState, RootState> = {
    namespaced: true,
    state: {
        loading: false,
        items: [],
    },
    getters: {
        loading: (state): boolean => state.loading,
        items: (state): Order[] => state.items,
        products: (state): OrderProduct[] => state.items.reduce((result: OrderProduct[], order) => result.concat(order.products), [])
    },
    mutations: {
        setLoading: (state, loading: boolean) => state.loading = loading,
        setItems: (state, items: Order[]) => state.items = items,
        addItem: (state, item: Order) => state.items.push(item),
    },
    actions: {
        fetch(context, params: Record<string, unknown>) {

            context.commit("setLoading", true);

            return retryRequest(() => order.get<{results: OrderResponseItem[]}>(`/users/${this.getters["user/id"]}/orders`, {params}))
                .then(response => {

                    context.commit("setItems", format(response.data.results));
                    context.commit("setLoading", false);
                });
        },
        loadItems(context, params: Record<string, unknown>): Promise<PaginatedResult<OrderProduct>> {

            return order
                .get<{results: OrderProduct[], pagination: {totalCount: number}}>(`/users/${this.getters["user/id"]}/items`, {params})
                .then(response => ({items: response.data.results, total: response.data.pagination.totalCount}));
        },
        // 400/409 (invalid order) are not retried
        post(context, model: Record<string, unknown>) {

            return retryRequest(
                () => order.post(`/users/${this.getters["user/id"]}/orders`, model),
                [1000, 2000],
                error => !error.response || (409 !== error.response.status && 400 !== error.response.status)
            ).then(result => {

                // Add timeout here to avoid race conditions issue and have all new products listed in support widget
                setTimeout(() => emitter.emit('user_order_submit'), 3000);

                return result;
            });
        },
        cancel(context, model: {id: string, [key: string]: unknown}) {

            return retryRequest(
                () => order.put(`/users/${this.getters["user/id"]}/items/${model.id}/cancel`, model),
                [1000, 2000]
            ).then(() => emitter.emit('user_order_cancel'));
        },
        received(context, orderProductId: string) {

            return retryRequest(
                () => order.put(`/users/${this.getters["user/id"]}/items/${orderProductId}/received`),
                [1000, 2000]
            ).then(() => emitter.emit('user_order_received'))
        },
        notReceived(context, orderProductId: string) {

            return retryRequest(
                () => order.put(`/users/${this.getters["user/id"]}/items/${orderProductId}/not-received`),
                [1000, 2000]
            ).then(() => emitter.emit('user_order_not_received'))
        },
        undo(context, orderProductId: string) {

            return retryRequest(
                () => order.post(`/users/${this.getters["user/id"]}/items/${orderProductId}/status/undo`),
                [1000, 2000]
            ).then(() => emitter.emit('user_order_undo'))
        },
        hide(context, orderProductId: string) {

            return retryRequest(
                () => order.post(`/users/${this.getters["user/id"]}/items/${orderProductId}/archive`),
                [1000, 2000]
            ).then(() => emitter.emit('user_order_hide'))
        },
        // 404 (unknown recipient) is not retried
        transfer(context, {orderProductId, email, reason}: OrderTransferPayload) {

            return retryRequest(
                () => order.put(`/users/${this.getters["user/id"]}/items/${orderProductId}/transfer`, {email, reason}),
                [1000, 2000],
                error => !error.response || 404 !== error.response.status
            ).then(() => emitter.emit('user_order_transfer'))
        },
        getRequirements(context, {items, country}: OrderRequirementsPayload): Promise<string[]> {

            return retryRequest(
                () => order.post<{results: {tags: string[]}}>(`/users/${this.getters["user/id"]}/orders/requirements`, {
                    country,
                    items: items.map(item => ({sku: item.sku, quantity: item.quantity}))
                }),
                [1000, 2000]
            ).then(response => response.data.results.tags)
        }
    }
}

export default orders
